import PropTypes from "prop-types";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import groupService from "../services/groupService";

const GroupContext = createContext();

export function GroupProvider({ children }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // AppSidebar lives inside AppShell, which every page renders itself, so
  // AppSidebar remounts (and re-fires its fetch-on-mount effect) on every
  // route change. Tracks whether a fetch has ever completed so that
  // remount-triggered calls can skip re-fetching already-loaded data,
  // without blocking real refreshes (create/update/delete below, and the
  // membership mutations, all still call fetchGroups directly).
  const hasFetchedRef = useRef(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await groupService.list();
      setGroups(data);
      hasFetchedRef.current = true;
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load groups");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // For mount-time callers (AppSidebar) that just want the sidebar
  // populated, not a guaranteed-fresh refresh — skips the request
  // entirely once groups have loaded at least once.
  const ensureGroupsLoaded = useCallback(() => {
    if (hasFetchedRef.current) return Promise.resolve(groups);
    return fetchGroups();
  }, [fetchGroups, groups]);

  const createGroup = useCallback(async (name) => {
    const data = await groupService.create(name);
    setGroups((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    return data;
  }, []);

  const updateGroup = useCallback(async (id, name) => {
    const data = await groupService.update(id, name);
    setGroups((prev) =>
      prev.map((g) => (g.id === id ? data : g)).sort((a, b) => a.name.localeCompare(b.name))
    );
    return data;
  }, []);

  const deleteGroup = useCallback(async (id) => {
    await groupService.delete(id);
    setGroups((prev) => prev.filter((g) => g.id !== id));
  }, []);

  const addPropertiesToGroup = useCallback(async (id, propertyIds) => {
    const data = await groupService.addProperties(id, propertyIds);
    await fetchGroups();
    return data;
  }, [fetchGroups]);

  const removePropertyFromGroup = useCallback(async (id, propertyId) => {
    await groupService.removeProperty(id, propertyId);
    await fetchGroups();
  }, [fetchGroups]);

  const value = useMemo(
    () => ({
      groups,
      loading,
      error,
      fetchGroups,
      ensureGroupsLoaded,
      createGroup,
      updateGroup,
      deleteGroup,
      addPropertiesToGroup,
      removePropertyFromGroup,
    }),
    [groups, loading, error, fetchGroups, ensureGroupsLoaded, createGroup, updateGroup, deleteGroup, addPropertiesToGroup, removePropertyFromGroup]
  );

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
}

GroupProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useGroupContext = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error("useGroupContext must be used within GroupProvider");
  }
  return context;
};

export default GroupContext;
